import React, { useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { STATUS_LABELS, FLOW } from "@/components/Status";
import { ORDER } from "@/constants/testIds";

/** Admin-only status update card. Closed is offered only after the order
 *  reaches Ready — matches the server-side gate in `update_status`. */
export default function StatusControl({ order, onUpdated }) {
  const [statusInput, setStatusInput] = useState(order.status);
  const [statusNote, setStatusNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const options = useMemo(() => {
    // Always offer the active flow + Cancelled.
    const opts = [...FLOW, "cancelled"];
    // Offer `closed` only when the order is already at `ready` or `closed`.
    if (order.status === "ready" || order.status === "closed") {
      opts.splice(opts.indexOf("cancelled"), 0, "closed");
    }
    return opts;
  }, [order.status]);

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.patch(`/orders/${order.order_id}/status`, {
        status: statusInput,
        internal_note: statusNote || undefined,
        notify_member: notify,
      });
      onUpdated?.(data);
      setStatusNote("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface-card p-6">
      <div className="overline">Pro Shop Controls</div>
      <h3 className="font-serif text-xl mt-1 text-[#1A1A1A]">Update Status</h3>
      <select
        value={statusInput}
        onChange={(e) => setStatusInput(e.target.value)}
        data-testid={ORDER.detailStatusSelect}
        className="input-field mt-4"
      >
        {options.map((s) => (
          <option key={s} value={s}>{STATUS_LABELS[s]}</option>
        ))}
      </select>
      {order.status === "ready" && (
        <p className="mt-2 text-xs text-[#7A7A7A]">
          Mark <strong>Closed</strong> only after the member has physically picked up the order.
        </p>
      )}
      <textarea
        rows={2}
        value={statusNote}
        onChange={(e) => setStatusNote(e.target.value)}
        placeholder="Optional internal note with this status change"
        className="input-field mt-3"
      />
      <label className="mt-3 inline-flex items-center gap-2 text-sm text-[#4A4A4A] cursor-pointer">
        <input
          type="checkbox"
          checked={notify}
          onChange={(e) => setNotify(e.target.checked)}
          data-testid={ORDER.detailNotifyMember}
          className="accent-[#1A422D] w-4 h-4"
        />
        Email member about this change
      </label>
      <button
        type="button"
        onClick={save}
        disabled={busy}
        data-testid={ORDER.detailStatusUpdate}
        className="btn-primary w-full mt-3"
      >
        {busy ? "Saving…" : "Save Status"}
      </button>
      {error && (
        <div className="mt-3 px-3 py-2 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-xs">
          {error}
        </div>
      )}
    </div>
  );
}
